import { PROPERTY_TYPES, type PropertyType } from '@erp/shared';
import { X } from 'lucide-react';
import { useState } from 'react';

import { useDistrictSearch } from '../../lib/contacts-api.ts';
import { Combobox } from '../ui/combobox.tsx';
import { Field, Input } from '../ui/field.tsx';

export interface InterestValue {
  propertyTypes: PropertyType[];
  districts: { id: number; name: string }[];
  budgetMin: string;
  budgetMax: string;
  bedroomsMin: string;
  bathroomsMin: string;
  parkingMin: string;
}

export const emptyInterest: InterestValue = {
  propertyTypes: [],
  districts: [],
  budgetMin: '',
  budgetMax: '',
  bedroomsMin: '',
  bathroomsMin: '',
  parkingMin: '',
};

const toNumber = (v: string) => (v.trim() === '' ? null : Number(v));

/** The API body for an interest ("" → null). */
export function interestBody(v: InterestValue) {
  return {
    propertyTypes: v.propertyTypes,
    districtIds: v.districts.map((d) => d.id),
    budgetMin: toNumber(v.budgetMin),
    budgetMax: toNumber(v.budgetMax),
    bedroomsMin: toNumber(v.bedroomsMin),
    bathroomsMin: toNumber(v.bathroomsMin),
    parkingMin: toNumber(v.parkingMin),
  };
}

/** HU-CRM-14: what the client is looking for (types, zones, budget, rooms). */
export function InterestFields({
  value,
  onChange,
  errors = {},
}: {
  value: InterestValue;
  onChange: (value: InterestValue) => void;
  errors?: Record<string, string>;
}) {
  const [query, setQuery] = useState('');
  const districts = useDistrictSearch(query);
  const set = (patch: Partial<InterestValue>) => {
    onChange({ ...value, ...patch });
  };
  const number = (
    key: 'budgetMin' | 'budgetMax' | 'bedroomsMin' | 'bathroomsMin' | 'parkingMin',
    label: string,
    step = '1',
  ) => (
    <Field label={label} error={errors[key]}>
      {(a11y) => (
        <Input
          {...a11y}
          type="number"
          inputMode="decimal"
          min={0}
          step={step}
          value={value[key]}
          onChange={(e) => {
            set({ [key]: e.target.value });
          }}
        />
      )}
    </Field>
  );
  return (
    <div className="space-y-4">
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Tipo de inmueble</legend>
        <div className="flex flex-wrap gap-x-5 gap-y-1">
          {(Object.entries(PROPERTY_TYPES) as [PropertyType, { label: string }][]).map(
            ([type, t]) => (
              <label key={type} className="flex min-h-11 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-5"
                  checked={value.propertyTypes.includes(type)}
                  onChange={(e) => {
                    set({
                      propertyTypes: e.target.checked
                        ? [...value.propertyTypes, type]
                        : value.propertyTypes.filter((x) => x !== type),
                    });
                  }}
                />
                {t.label}
              </label>
            ),
          )}
        </div>
      </fieldset>
      <Field label="Zonas (distritos)" hint="Agrega los distritos que le interesan.">
        {(a11y) => (
          <Combobox
            id={a11y.id}
            describedBy={a11y['aria-describedby']}
            query={query}
            onQueryChange={setQuery}
            loading={districts.isFetching}
            options={(districts.data ?? [])
              .filter((d) => !value.districts.some((x) => x.id === d.id))
              .map((d) => ({
                id: String(d.id),
                label: d.name,
                detail: `${d.municipality.name}, ${d.department.name}`,
              }))}
            onSelect={(option) => {
              set({
                districts: [...value.districts, { id: Number(option.id), name: option.label }],
              });
              setQuery('');
            }}
            placeholder="Santa Tecla, Escalón…"
          />
        )}
      </Field>
      {value.districts.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {value.districts.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-1 rounded-full bg-soft py-0.5 pr-0.5 pl-2.5 text-sm text-soft-foreground"
            >
              {d.name}
              <button
                type="button"
                className="flex size-6 items-center justify-center rounded-full hover:bg-surface"
                aria-label={`Quitar ${d.name}`}
                onClick={() => {
                  set({ districts: value.districts.filter((x) => x.id !== d.id) });
                }}
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {number('budgetMin', 'Presupuesto mínimo (USD)', '0.01')}
        {number('budgetMax', 'Presupuesto máximo (USD)', '0.01')}
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {number('bedroomsMin', 'Habitaciones (mínimo)')}
        {number('bathroomsMin', 'Baños (mínimo)', '0.5')}
        {number('parkingMin', 'Parqueos (mínimo)')}
      </div>
    </div>
  );
}
